import React from "react";
import { FaramListElement } from "@togglecorp/faram";
import { _cs } from "@togglecorp/fujs";

import EmptyWhenFilter from "../../EmptyWhenFilter";
import NormalList from "#rscv/List/index";
import Message from "../../Message";
import LoadingAnimation from "../../LoadingAnimation";

import styles from "./styles.module.scss";

// ---------------------------------------------------------------------
// 1. Default Empty Component
// ---------------------------------------------------------------------
const DefaultEmptyComponent: React.FC = () => {
  const classNames = _cs("empty", styles.empty);

  return (
    <div className={classNames}>
      <Message>Nothing to show.</Message>
    </div>
  );
};

// ---------------------------------------------------------------------
// 2. Data Item Type (matches propTypeData)
// ---------------------------------------------------------------------
export type ListDataItem =
  | string
  | number
  | { dummy?: string; [key: string]: unknown }
  | readonly unknown[];

// ---------------------------------------------------------------------
// 3. Props (replaces prop-types)
// ---------------------------------------------------------------------
export interface NormalListViewProps {
  className?: string;
  data?: readonly ListDataItem[];
  emptyComponent?: React.ComponentType;
  id?: string;
  isFiltered?: boolean;
  pending?: boolean;
  [key: string]: unknown; // for renderer, keySelector, etc.
}

// ---------------------------------------------------------------------
// 4. Inner Component (functional)
// ---------------------------------------------------------------------
const NormalListViewInner: React.FC<NormalListViewProps> = ({
  className: classNameFromProps = "",
  data = [],
  emptyComponent: EmptyComponent = DefaultEmptyComponent,
  id,
  pending = false,
  isFiltered = false,
  ...otherProps
}) => {
  const isEmpty = data.length === 0;

  // -----------------------------------------------------------------
  // 5. Root className
  // -----------------------------------------------------------------
  const rootClassName = _cs(
    classNameFromProps,
    styles.listView,
    "list-view",
    pending && styles.pending,
    isEmpty && "list-view-empty",
    isEmpty && styles.listViewEmpty
  );

  // -----------------------------------------------------------------
  // 6. Content logic
  // -----------------------------------------------------------------
  let content: React.ReactNode = null;

  if (isEmpty) {
    if (isFiltered) {
      content = !pending ? <EmptyWhenFilter className={styles.empty} /> : null;
    } else {
      content = EmptyComponent && !pending ? <EmptyComponent /> : null;
    }
  } else {
    content = <NormalList data={data} {...otherProps} />;
  }

  // -----------------------------------------------------------------
  // 7. Render
  // -----------------------------------------------------------------
  return (
    <div className={rootClassName} id={id}>
      {pending && <LoadingAnimation />}
      {content}
    </div>
  );
};

// ---------------------------------------------------------------------
// 8. Apply Faram HOC
// ---------------------------------------------------------------------
const NormalListView = FaramListElement(NormalListViewInner);

export default NormalListView;
